// src/contexts/CouponRedemptionContext.jsx
//
// Student side of coupon redemption: scan (or type) a coupon code,
// preview what it's worth via lookupCoupon(), then commit with
// redeemCoupon() which credits the student's wallet for that
// school_program — added on top of any existing balance, never
// replacing it. Mirrors SchoolProgramsContext.jsx's shape/pattern
// exactly (same apiFetch helper, same conventions) so it drops into
// the app the same way, just pointed at /api/coupons/*.
import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { useUser } from './UserContext';

const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, '');
const FALLBACK_BASE = 'https://www.doroosonline.co.il';
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;
const url = (path) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

const DEFAULT_TIMEOUT_MS = 12000;

const getStoredToken = () => {
  try {
    return localStorage.getItem('jwt') ?? localStorage.getItem('token') ?? null;
  } catch {
    return null;
  }
};

async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const text = await res.text().catch(() => '');
  if (!text) return {};
  try { return JSON.parse(text); } catch { return { raw: text }; }
}

async function apiFetch(path, { method = 'GET', body, timeout = DEFAULT_TIMEOUT_MS } = {}) {
  const token = getStoredToken();
  const hasBody = body !== undefined;

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url(path), {
      method,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: hasBody ? JSON.stringify(body) : undefined,
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });

    const data = await parseJSONSafely(res);

    if (!res.ok) {
      const msg =
        data?.error ||
        (typeof data?.error === 'object' ? Object.values(data.error)[0] : null) ||
        (res.status === 401 ? 'שגיאה בהרשאות (401)' : `שגיאת שרת (${res.status})`);
      throw new Error(msg);
    }

    return data ?? {};
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('זמן ההמתנה לשרת הסתיים. נסה שוב.');
    if (e instanceof TypeError) {
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת ב- ${API_BASE}.`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

const CouponRedemptionContext = createContext(null);

export const CouponRedemptionProvider = ({ children }) => {
  const { role, userType, showAlert } = useUser();
  const isStudent = role === 'student' || userType === 'student';

  // Read-only preview of a scanned/typed code, before committing —
  // lets the UI show "Redeem ₪150 towards <program>?" first.
  const lookupCoupon = useCallback(async (code) => {
    const data = await apiFetch(`/api/coupons/lookup?code=${encodeURIComponent(code)}`);
    return data;
  }, []);

  // Commits the redemption: credits the student's wallet for that
  // school_program (added to whatever balance was already there).
  const redeemCoupon = useCallback(async (code) => {
    const data = await apiFetch('/api/coupons/redeem', {
      method: 'POST',
      body: { code },
    });
    return data;
  }, []);

  // Everything the student can spend: general store credit
  // (creditBalance) plus a per-program GEFEN wallet breakdown
  // (wallets) — for the standalone wallet page.
  const getWallets = useCallback(async () => {
    const data = await apiFetch('/api/coupons/wallets');
    return data;
  }, []);

  const value = useMemo(
    () => ({ isStudent, lookupCoupon, redeemCoupon, getWallets, showAlert }),
    [isStudent, lookupCoupon, redeemCoupon, getWallets, showAlert]
  );

  return (
    <CouponRedemptionContext.Provider value={value}>
      {children}
    </CouponRedemptionContext.Provider>
  );
};

export const useCouponRedemption = () => {
  const ctx = useContext(CouponRedemptionContext);
  if (!ctx) throw new Error('useCouponRedemption must be used within a CouponRedemptionProvider');
  return ctx;
};